import {
    AddOutlined,
    CloseOutlined,
    DescriptionOutlined,
    DownloadOutlined,
    FullscreenOutlined,
    HideImageOutlined,
    LocalGasStationOutlined,
    SaveOutlined,
    SpeedOutlined,
} from "@mui/icons-material";
import {
    Box,
    Button,
    Card,
    CardActions,
    Chip,
    CircularProgress,
    Fab,
    FormControlLabel,
    IconButton,
    InputAdornment,
    Paper,
    Stack,
    Switch,
    SwitchProps,
    TextField,
    Tooltip,
    Typography,
    styled,
} from "@mui/material";
import {
    ChangeEvent,
    Dispatch,
    ReactNode,
    SetStateAction,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { useCookies } from "react-cookie";
import findVehicleParametersByVehicleAndCustomer from "../../../../resources/Api/vehicleparameter/findVehicleParameterByVehicleAndCustomer";
import saveVehicleParameter from "../../../../resources/Api/vehicleparameter/saveVehicleParameter";
import {
    VehicleAttachment,
    VehicleParameter as VehicleParameterModel,
} from "../../../../types/types";
import {
    buildVehicleParameterPayload,
    createInitialVehicleParameter,
    normalizeVehicleParameterResponse,
} from "./vehicleParameterModel";

type StatusMessage = "warn" | "success" | "info" | "error" | undefined;
type VehicleParameterSection = "parameters" | "attachments";

type VehicleParameterProps = {
    vehicleId: number;
    customerId: number;
    section: VehicleParameterSection;
    isDisabled: boolean;
    setTextValidation: Dispatch<SetStateAction<string>>;
    setStatusMessage: Dispatch<SetStateAction<StatusMessage>>;
    setFieldsIsValid: Dispatch<SetStateAction<boolean>>;
    onUnsavedChangesChange: (hasUnsavedChanges: boolean) => void;
    onClose: () => void;
};

type NumberFieldProps = {
    label: string;
    placeholder: string;
    value?: number | null;
    onChange: (value: number | null) => void;
    icon?: ReactNode;
    disabled?: boolean;
    decimal?: boolean;
};

type AttachmentCardProps = {
    attachment: VehicleAttachment;
    editableDescription?: boolean;
    onDescriptionChange?: (description: string) => void;
    onDownload: () => void;
    onFullscreen: () => void;
    onRemove?: () => void;
};

const MAX_ATTACHMENTS = 3;

const GreenSwitch = styled((props: SwitchProps) => (
    <Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...props} />
))(({ theme }) => ({
    width: 42,
    height: 26,
    padding: 0,
    "& .MuiSwitch-switchBase": {
        padding: 0,
        margin: 2,
        transitionDuration: "300ms",
        "&.Mui-checked": {
            transform: "translateX(16px)",
            color: "#fff",
            "& + .MuiSwitch-track": {
                backgroundColor: "#65C466",
                opacity: 1,
                border: 0,
            },
        },
        "&.Mui-focusVisible .MuiSwitch-thumb": {
            color: "#33cf4d",
            border: "6px solid #fff",
        },
        "&.Mui-disabled .MuiSwitch-thumb": {
            color: theme.palette.grey[100],
        },
        "&.Mui-disabled + .MuiSwitch-track": {
            opacity: 0.5,
        },
    },
    "& .MuiSwitch-thumb": {
        boxSizing: "border-box",
        width: 22,
        height: 22,
    },
    "& .MuiSwitch-track": {
        borderRadius: 13,
        backgroundColor: "#E9E9EA",
        opacity: 1,
        transition: theme.transitions.create(["background-color"], {
            duration: 500,
        }),
    },
}));

const getApiErrorMessage = (error: any, fallback: string): string => {
    const reason = error?.response?.data?.reason;

    if (Array.isArray(reason)) {
        return reason.filter(Boolean).join(". ") || fallback;
    }

    return typeof reason === "string" && reason.trim() ? reason : fallback;
};

const NumberField = ({
    label,
    placeholder,
    value,
    onChange,
    icon,
    disabled = false,
    decimal = false,
}: NumberFieldProps) => (
    <TextField
        fullWidth
        type="number"
        autoComplete="off"
        label={label}
        value={value ?? ""}
        placeholder={placeholder}
        InputLabelProps={{ shrink: true }}
        InputProps={{
            endAdornment: icon ? (
                <InputAdornment position="end">{icon}</InputAdornment>
            ) : undefined,
        }}
        inputProps={{
            min: 0,
            step: decimal ? "0.01" : 1,
        }}
        onChange={(event) =>
            onChange(event.target.value === "" ? null : Number(event.target.value))
        }
        disabled={disabled}
    />
);

const AttachmentCard = ({
    attachment,
    editableDescription = false,
    onDescriptionChange,
    onDownload,
    onFullscreen,
    onRemove,
}: AttachmentCardProps) => (
    <Card
        className="tw-overflow-hidden tw-rounded-xl tw-border tw-border-black/10 tw-bg-white tw-shadow-sm tw-transition-all tw-duration-200 hover:tw-translate-y-[-1px] hover:tw-shadow-md"
        sx={{
            width: 190,
            background: "#ffff",
            padding: 0.5,
            margin: 0.5,
            border: "1px solid rgba(0, 63, 74, 0.10)",
            borderRadius: 3,
            boxShadow: "0 2px 10px rgba(0, 0, 0, 0.06)",
            transition: "transform 200ms ease, box-shadow 200ms ease",
        }}
    >
        {attachment.file ? (
            <Box
                component="img"
                src={attachment.file}
                alt={attachment.name || attachment.description || "Anexo do veículo"}
                sx={{
                    objectFit: "cover",
                    width: 170,
                    height: 150,
                    display: "block",
                    borderRadius: 2,
                    background: "gray",
                    margin: "5px auto",
                }}
            />
        ) : (
            <Stack
                alignItems="center"
                justifyContent="center"
                sx={{ width: 170, height: 150, margin: "5px auto" }}
            >
                <HideImageOutlined sx={{ fontSize: 44, color: "#6c757d" }} />
                <Typography variant="caption">Imagem indisponível</Typography>
            </Stack>
        )}

        {editableDescription ? (
            <TextField
                fullWidth
                size="small"
                label="Descrição do anexo"
                value={attachment.description ?? ""}
                placeholder="Digite uma descrição (Opcional)"
                InputLabelProps={{ shrink: true }}
                inputProps={{ maxLength: 250 }}
                onChange={(event) => onDescriptionChange?.(event.target.value)}
                sx={{ marginTop: 1 }}
            />
        ) : (
            <Typography
                variant="caption"
                title={attachment.description || attachment.name}
                sx={{
                    display: "block",
                    fontFamily: "Prometo",
                    textAlign: "center",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    padding: "4px 8px 0",
                }}
            >
                {attachment.description || attachment.name || `Anexo #${attachment.id}`}
            </Typography>
        )}

        <CardActions sx={{ justifyContent: "center" }}>
            <Tooltip title="Baixar anexo">
                <span>
                    <IconButton
                        size="small"
                        onClick={onDownload}
                        disabled={!attachment.file}
                        aria-label="Baixar anexo do veículo"
                    >
                        <DownloadOutlined sx={{ color: "#00B700" }} />
                    </IconButton>
                </span>
            </Tooltip>

            {onRemove && (
                <Tooltip title="Remover anexo">
                    <IconButton
                        size="small"
                        onClick={onRemove}
                        aria-label="Remover anexo do veículo"
                    >
                        <HideImageOutlined sx={{ color: "red" }} />
                    </IconButton>
                </Tooltip>
            )}

            <Tooltip title="Visualizar em tela cheia">
                <span>
                    <IconButton
                        size="small"
                        onClick={onFullscreen}
                        disabled={!attachment.file}
                        aria-label="Visualizar anexo do veículo em tela cheia"
                    >
                        <FullscreenOutlined sx={{ color: "#1976d2" }} />
                    </IconButton>
                </span>
            </Tooltip>
        </CardActions>
    </Card>
);

const VehicleParameter = ({
    vehicleId,
    customerId,
    section,
    isDisabled,
    setTextValidation,
    setStatusMessage,
    setFieldsIsValid,
    onUnsavedChangesChange,
    onClose,
}: VehicleParameterProps) => {
    const [cookies] = useCookies(["token"]);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const [vehicleParameter, setVehicleParameter] = useState<VehicleParameterModel>(
        createInitialVehicleParameter(vehicleId, customerId),
    );
    const [initialVehicleParameter, setInitialVehicleParameter] =
        useState<VehicleParameterModel | null>(null);
    const [newFiles, setNewFiles] = useState<VehicleAttachment[]>([]);
    const [removedFiles, setRemovedFiles] = useState<number[]>([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const showMessage = useCallback(
        (message: string, status: StatusMessage) => {
            setTextValidation(message);
            setStatusMessage(status);
            setFieldsIsValid(false);
        },
        [setFieldsIsValid, setStatusMessage, setTextValidation],
    );

    const resetAttachmentChanges = useCallback(() => {
        setNewFiles([]);
        setRemovedFiles([]);

        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    }, []);

    const loadVehicleParameter = useCallback(async () => {
        const token: string = cookies.token || sessionStorage.getItem("token");
        const emptyParameter = createInitialVehicleParameter(vehicleId, customerId);

        if (vehicleId <= 0 || customerId <= 0) {
            setVehicleParameter(emptyParameter);
            setInitialVehicleParameter(emptyParameter);
            resetAttachmentChanges();
            return;
        }

        setLoading(true);

        try {
            const response = await findVehicleParametersByVehicleAndCustomer(
                token,
                vehicleId,
                customerId,
            );
            const loadedParameter = normalizeVehicleParameterResponse(
                response.data,
                vehicleId,
                customerId,
            );

            setVehicleParameter(loadedParameter);
            setInitialVehicleParameter(loadedParameter);
            resetAttachmentChanges();
            onUnsavedChangesChange(false);
        } catch (error: any) {
            if (error?.response?.status === 404) {
                setVehicleParameter(emptyParameter);
                setInitialVehicleParameter(emptyParameter);
                resetAttachmentChanges();
                onUnsavedChangesChange(false);
            } else {
                console.error("Erro ao buscar parâmetros do veículo:", error);
                setVehicleParameter(emptyParameter);
                setInitialVehicleParameter(emptyParameter);
                resetAttachmentChanges();
                showMessage(
                    getApiErrorMessage(
                        error,
                        "Não foi possível carregar os parâmetros do veículo.",
                    ),
                    "error",
                );
            }
        } finally {
            setLoading(false);
        }
    }, [
        cookies.token,
        customerId,
        onUnsavedChangesChange,
        resetAttachmentChanges,
        showMessage,
        vehicleId,
    ]);

    useEffect(() => {
        void loadVehicleParameter();
    }, [loadVehicleParameter]);

    const hasChanges = useCallback(() => {
        if (!initialVehicleParameter) {
            return false;
        }

        const normalizeNumber = (value?: number | null) =>
            value == null || Number.isNaN(Number(value)) ? null : Number(value);
        const normalizeString = (value?: string | null) => value?.trim() ?? "";

        return (
            normalizeNumber(vehicleParameter.currentOdometer) !==
            normalizeNumber(initialVehicleParameter.currentOdometer) ||
            normalizeNumber(vehicleParameter.fuelTankCapacity) !==
            normalizeNumber(initialVehicleParameter.fuelTankCapacity) ||
            normalizeString(vehicleParameter.observation) !==
            normalizeString(initialVehicleParameter.observation) ||
            vehicleParameter.useAlert !== initialVehicleParameter.useAlert ||
            normalizeNumber(vehicleParameter.maintenanceAlertDays) !==
            normalizeNumber(initialVehicleParameter.maintenanceAlertDays) ||
            normalizeNumber(vehicleParameter.maintenanceAlertKm) !==
            normalizeNumber(initialVehicleParameter.maintenanceAlertKm) ||
            normalizeNumber(vehicleParameter.oilChangeAlertDays) !==
            normalizeNumber(initialVehicleParameter.oilChangeAlertDays) ||
            normalizeNumber(vehicleParameter.oilChangeAlertKm) !==
            normalizeNumber(initialVehicleParameter.oilChangeAlertKm) ||
            newFiles.length > 0 ||
            removedFiles.length > 0
        );
    }, [initialVehicleParameter, newFiles.length, removedFiles.length, vehicleParameter]);

    useEffect(() => {
        onUnsavedChangesChange(hasChanges());
    }, [hasChanges, onUnsavedChangesChange]);

    useEffect(
        () => () => onUnsavedChangesChange(false),
        [onUnsavedChangesChange],
    );

    const validate = useCallback((): string | null => {
        if (vehicleId <= 0) {
            return "Veículo inválido, selecione um veículo e tente novamente.";
        }

        if (customerId <= 0) {
            return "Cliente inválido.";
        }

        const integerFields: Array<[number | null | undefined, string]> = [
            [vehicleParameter.currentOdometer, "O hodômetro atual"],
            [vehicleParameter.maintenanceAlertDays, "O alerta de manutenção em dias"],
            [vehicleParameter.maintenanceAlertKm, "O alerta de manutenção em Km"],
            [vehicleParameter.oilChangeAlertDays, "O alerta de troca de óleo em dias"],
            [vehicleParameter.oilChangeAlertKm, "O alerta de troca de óleo em Km"],
        ];

        for (const [value, label] of integerFields) {
            if (
                value != null &&
                (!Number.isFinite(Number(value)) ||
                    Number(value) < 0 ||
                    !Number.isInteger(Number(value)))
            ) {
                return `${label} deve ser um número inteiro maior ou igual a zero.`;
            }
        }

        if (
            vehicleParameter.fuelTankCapacity != null &&
            (!Number.isFinite(Number(vehicleParameter.fuelTankCapacity)) ||
                Number(vehicleParameter.fuelTankCapacity) < 0)
        ) {
            return "A capacidade do tanque deve ser maior ou igual a zero.";
        }

        if ((vehicleParameter.observation?.trim().length ?? 0) > 500) {
            return "A observação deve possuir no máximo 500 caracteres.";
        }

        if (newFiles.some((file) => (file.description?.trim().length ?? 0) > 250)) {
            return "A descrição do anexo deve possuir no máximo 250 caracteres.";
        }

        return null;
    }, [customerId, newFiles, vehicleId, vehicleParameter]);

    const handleSave = useCallback(async () => {
        const token: string = cookies.token || sessionStorage.getItem("token");
        const validationMessage = validate();

        if (validationMessage) {
            showMessage(validationMessage, "warn");
            return;
        }

        if (initialVehicleParameter?.id != null && !hasChanges()) {
            showMessage("Nada foi alterado.", "warn");
            return;
        }

        const payload = buildVehicleParameterPayload({
            vehicleParameter,
            vehicleId,
            customerId,
            newFiles,
            removedFiles,
        });

        setSaving(true);

        try {
            await saveVehicleParameter(token, payload);
            await loadVehicleParameter();
            showMessage(
                section === "attachments"
                    ? "Anexos do veículo salvos com sucesso."
                    : "Parâmetros do veículo salvos com sucesso.",
                "success",
            );
        } catch (error: any) {
            console.error("Erro ao salvar parâmetros do veículo:", error);
            showMessage(
                getApiErrorMessage(
                    error,
                    section === "attachments"
                        ? "Erro ao salvar os anexos do veículo."
                        : "Erro ao salvar os parâmetros do veículo.",
                ),
                error?.response?.data?.reason ? "warn" : "error",
            );
        } finally {
            setSaving(false);
        }
    }, [
        cookies.token,
        customerId,
        hasChanges,
        initialVehicleParameter?.id,
        loadVehicleParameter,
        newFiles,
        removedFiles,
        section,
        showMessage,
        validate,
        vehicleId,
        vehicleParameter,
    ]);

    const visibleExistingFiles = useMemo(
        () =>
            vehicleParameter.files?.filter(
                (file) =>
                    file.activated !== false &&
                    file.id != null &&
                    !removedFiles.includes(file.id),
            ) ?? [],
        [removedFiles, vehicleParameter.files],
    );
    const attachmentCount = visibleExistingFiles.length + newFiles.length;

    const handleFileSelection = useCallback(
        (event: ChangeEvent<HTMLInputElement>) => {
            const selectedFile = event.target.files?.[0];

            if (!selectedFile || !selectedFile.type.startsWith("image/")) {
                showMessage("Selecione um arquivo de imagem válido.", "warn");
                event.target.value = "";
                return;
            }

            if (attachmentCount >= MAX_ATTACHMENTS) {
                showMessage(
                    `É permitido anexar no máximo ${MAX_ATTACHMENTS} imagens.`,
                    "warn",
                );
                event.target.value = "";
                return;
            }

            const reader = new FileReader();
            reader.readAsDataURL(selectedFile);
            reader.onloadend = () => {
                if (typeof reader.result !== "string") {
                    showMessage("Não foi possível ler o anexo selecionado.", "error");
                    return;
                }

                setNewFiles((previous) => [
                    ...previous,
                    {
                        clientId: `${Date.now()}-${Math.random()}`,
                        file: reader.result as string,
                        description: "",
                        name: selectedFile.name,
                        type: selectedFile.type,
                        activated: true,
                    },
                ]);

                if (fileInputRef.current) {
                    fileInputRef.current.value = "";
                }
            };
            reader.onerror = () => {
                showMessage("Não foi possível ler o anexo selecionado.", "error");
                event.target.value = "";
            };
        },
        [attachmentCount, showMessage],
    );

    const handleFileDownload = useCallback((fileUrl: string, fileName: string) => {
        const link = document.createElement("a");
        link.href = fileUrl;

        let extension = "";
        if (fileUrl.startsWith("data:")) {
            const mime = fileUrl.split(";")[0].split(":")[1];
            if (mime?.includes("/")) {
                extension = `.${mime.split("/")[1]}`;
            }
        }

        link.download = fileName.includes(".")
            ? fileName
            : `${fileName || "anexo"}${extension || ".png"}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }, []);

    const handleFileFullscreen = useCallback((fileUrl: string, fileName: string) => {
        if (document.fullscreenElement) {
            void document.exitFullscreen();
            return;
        }

        const element = document.createElement("img");
        element.className = "vehicle-parameter-img-anexo";
        element.id = fileName;
        element.src = fileUrl;
        element.style.objectFit = "contain";
        element.style.width = "100%";
        element.style.height = "100%";
        document.body.appendChild(element);

        void element.requestFullscreen().catch((error) => {
            console.error("Erro ao abrir anexo em tela cheia:", error);
            if (document.body.contains(element)) {
                document.body.removeChild(element);
            }
        });

        element.addEventListener("fullscreenchange", () => {
            if (!document.fullscreenElement && document.body.contains(element)) {
                document.body.removeChild(element);
            }
        });
    }, []);

    if (loading) {
        return (
            <Paper className="tw-rounded-xl tw-bg-white tw-p-6" elevation={0} sx={{ background: "#fff", padding: 4, borderRadius: 3 }}>
                <Stack
                    direction="row"
                    spacing={1.5}
                    alignItems="center"
                    justifyContent="center"
                    role="status"
                    aria-live="polite"
                >
                    <CircularProgress size={24} sx={{ color: "#00B700" }} />
                    <Typography sx={{ fontFamily: "Prometo" }}>
                        {section === "attachments"
                            ? "Carregando anexos do veículo..."
                            : "Carregando parâmetros do veículo..."}
                    </Typography>
                </Stack>
            </Paper>
        );
    }

    return (
        <>
            {section === "parameters" ? (
                <Paper
                    className="tw-rounded-xl tw-border tw-border-black/10 tw-bg-white tw-p-4 sm:tw-p-5"
                    elevation={0}
                    sx={{
                        background: "#fff",
                        padding: { xs: 2, sm: 2.5 },
                        width: "100%",
                        border: "1px solid rgba(0, 63, 74, 0.10)",
                        borderRadius: 3,
                        "& .MuiOutlinedInput-root": { borderRadius: 2, backgroundColor: "#ffff", transition: "box-shadow 200ms ease, background-color 200ms ease" },
                        "& .MuiOutlinedInput-root:hover": { backgroundColor: "rgba(0, 108, 119, 0.02)" },
                        "& .MuiOutlinedInput-root.Mui-focused": { boxShadow: "0 0 0 3px rgba(0, 108, 119, 0.10)" },
                        "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#006C77" },
                    }}
                >
                    <Box className="row tw-m-0" sx={{ margin: 0 }}>
                        <Box className="col-md-6" sx={{ margin: "10px 0" }}>
                            <NumberField
                                label="Hodômetro atual (Km)"
                                placeholder="Digite o valor do hodômetro"
                                value={vehicleParameter.currentOdometer}
                                onChange={(value) =>
                                    setVehicleParameter((previous) => ({
                                        ...previous,
                                        currentOdometer: value,
                                    }))
                                }
                                icon={<SpeedOutlined />}
                                disabled={isDisabled}
                            />
                        </Box>

                        <Box className="col-md-6" sx={{ margin: "10px 0" }}>
                            <NumberField
                                label="Capacidade do tanque (L)"
                                placeholder="Digite a capacidade em litros"
                                value={vehicleParameter.fuelTankCapacity}
                                onChange={(value) =>
                                    setVehicleParameter((previous) => ({
                                        ...previous,
                                        fuelTankCapacity: value,
                                    }))
                                }
                                icon={<LocalGasStationOutlined />}
                                disabled={isDisabled}
                                decimal
                            />
                        </Box>

                        <Box className="col-md-12" sx={{ margin: "10px 0" }}>
                            <FormControlLabel
                                control={
                                    <GreenSwitch
                                        checked={vehicleParameter.useAlert}
                                        onChange={(event) =>
                                            setVehicleParameter((previous) => ({
                                                ...previous,
                                                useAlert: event.target.checked,
                                            }))
                                        }
                                        disabled={isDisabled}
                                    />
                                }
                                label="Receber alertas de manutenção"
                                sx={{
                                    marginLeft: 0,
                                    gap: 1,
                                    "& .MuiFormControlLabel-label": {
                                        fontFamily: "Prometo",
                                        color: "gray",
                                    },
                                }}
                            />
                        </Box>

                        <Box className="col-md-6" sx={{ margin: "10px 0" }}>
                            <NumberField
                                label="Alerta manutenção (dias)"
                                placeholder="Dias antes da manutenção"
                                value={vehicleParameter.maintenanceAlertDays}
                                onChange={(value) =>
                                    setVehicleParameter((previous) => ({
                                        ...previous,
                                        maintenanceAlertDays: value,
                                    }))
                                }
                                disabled={isDisabled || !vehicleParameter.useAlert}
                            />
                        </Box>

                        <Box className="col-md-6" sx={{ margin: "10px 0" }}>
                            <NumberField
                                label="Alerta manutenção (Km)"
                                placeholder="Km antes da manutenção"
                                value={vehicleParameter.maintenanceAlertKm}
                                onChange={(value) =>
                                    setVehicleParameter((previous) => ({
                                        ...previous,
                                        maintenanceAlertKm: value,
                                    }))
                                }
                                disabled={isDisabled || !vehicleParameter.useAlert}
                            />
                        </Box>

                        <Box className="col-md-6" sx={{ margin: "10px 0" }}>
                            <NumberField
                                label="Alerta troca de óleo (dias)"
                                placeholder="Dias antes da troca de óleo"
                                value={vehicleParameter.oilChangeAlertDays}
                                onChange={(value) =>
                                    setVehicleParameter((previous) => ({
                                        ...previous,
                                        oilChangeAlertDays: value,
                                    }))
                                }
                                disabled={isDisabled || !vehicleParameter.useAlert}
                            />
                        </Box>

                        <Box className="col-md-6" sx={{ margin: "10px 0" }}>
                            <NumberField
                                label="Alerta troca de óleo (Km)"
                                placeholder="Km antes da troca de óleo"
                                value={vehicleParameter.oilChangeAlertKm}
                                onChange={(value) =>
                                    setVehicleParameter((previous) => ({
                                        ...previous,
                                        oilChangeAlertKm: value,
                                    }))
                                }
                                disabled={isDisabled || !vehicleParameter.useAlert}
                            />
                        </Box>

                        <Box className="col-md-12" sx={{ margin: "10px 0" }}>
                            <TextField
                                fullWidth
                                multiline
                                rows={4}
                                label="Observação"
                                value={vehicleParameter.observation ?? ""}
                                placeholder="Digite a observação (Opcional)"
                                InputLabelProps={{ shrink: true }}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <DescriptionOutlined />
                                        </InputAdornment>
                                    ),
                                }}
                                inputProps={{ maxLength: 500 }}
                                helperText={`${vehicleParameter.observation?.length ?? 0}/500`}
                                onChange={(event) =>
                                    setVehicleParameter((previous) => ({
                                        ...previous,
                                        observation: event.target.value,
                                    }))
                                }
                                disabled={isDisabled}
                            />
                        </Box>
                    </Box>
                </Paper>
            ) : (
                <Paper
                    className="tw-rounded-xl tw-border tw-border-black/10 tw-bg-white tw-p-4 sm:tw-p-5"
                    elevation={0}
                    sx={{ padding: { xs: 2, sm: 2.5 }, background: "#fff", border: "1px solid rgba(0, 63, 74, 0.10)", borderRadius: 3 }}
                >
                    <Typography
                        sx={{ fontFamily: "Prometo", fontWeight: "bold", marginBottom: 1 }}
                    >
                        Anexos vinculados diretamente ao veículo
                    </Typography>
                    <Typography
                        variant="body2"
                        sx={{ fontFamily: "Prometo", color: "gray", marginBottom: 2 }}
                    >
                        Estes anexos são independentes dos anexos das manutenções e despesas.
                    </Typography>

                    <input
                        type="file"
                        hidden
                        ref={fileInputRef}
                        accept="image/*"
                        onChange={handleFileSelection}
                    />

                    <Box
                        className="attachment-dropzone tw-flex tw-min-h-[220px] tw-flex-wrap tw-content-start tw-items-start tw-justify-start tw-gap-2 tw-rounded-xl tw-border tw-border-dashed tw-border-black/15 tw-bg-slate-50/70 tw-p-4"
                        sx={{
                            minHeight: 220,
                            border: "1px dashed rgba(0, 63, 74, 0.20)",
                            borderRadius: 3,
                            backgroundColor: "rgba(248, 250, 252, 0.70)",
                            padding: 2,
                            display: "flex",
                            flexWrap: "wrap",
                            alignItems: "flex-start",
                            justifyContent: "flex-start",
                            gap: 1,
                        }}
                    >
                        {visibleExistingFiles.map((file, index) => (
                            <AttachmentCard
                                key={file.id}
                                attachment={file}
                                onDownload={() =>
                                    handleFileDownload(
                                        file.file,
                                        file.name || `anexo-${index + 1}`,
                                    )
                                }
                                onFullscreen={() =>
                                    handleFileFullscreen(
                                        file.file,
                                        file.name || `anexo-${index + 1}`,
                                    )
                                }
                                onRemove={
                                    !isDisabled && file.id != null
                                        ? () =>
                                            setRemovedFiles((previous) =>
                                                previous.includes(file.id as number)
                                                    ? previous
                                                    : [...previous, file.id as number],
                                            )
                                        : undefined
                                }
                            />
                        ))}

                        {newFiles.map((file, index) => (
                            <AttachmentCard
                                key={file.clientId}
                                attachment={file}
                                editableDescription={!isDisabled}
                                onDescriptionChange={(description) =>
                                    setNewFiles((previous) =>
                                        previous.map((item) =>
                                            item.clientId === file.clientId
                                                ? { ...item, description }
                                                : item,
                                        ),
                                    )
                                }
                                onDownload={() =>
                                    handleFileDownload(
                                        file.file,
                                        file.name || `anexo-${index + 1}`,
                                    )
                                }
                                onFullscreen={() =>
                                    handleFileFullscreen(
                                        file.file,
                                        file.name || `anexo-${index + 1}`,
                                    )
                                }
                                onRemove={
                                    !isDisabled
                                        ? () =>
                                            setNewFiles((previous) =>
                                                previous.filter(
                                                    (item) =>
                                                        item.clientId !== file.clientId,
                                                ),
                                            )
                                        : undefined
                                }
                            />
                        ))}

                        {attachmentCount === 0 && isDisabled && (
                            <Stack alignItems="center" spacing={1}>
                                <HideImageOutlined sx={{ fontSize: 44, color: "#6c757d" }} />
                                <Typography sx={{ fontFamily: "Prometo", color: "gray" }}>
                                    Nenhum anexo encontrado para este veículo.
                                </Typography>
                            </Stack>
                        )}

                        {!isDisabled && attachmentCount < MAX_ATTACHMENTS && (
                            <Box
                                sx={{
                                    minHeight: attachmentCount > 0 ? 170 : 45,
                                    display: "flex",
                                    alignItems:
                                        attachmentCount > 0 ? "center" : "flex-start",
                                }}
                            >
                                <Chip
                                    label="Anexar"
                                    sx={{
                                        color: "#00B700",
                                        "& .MuiChip-icon": { marginLeft: 0 },
                                    }}
                                    icon={
                                        <Fab
                                            size="small"
                                            aria-label="Adicionar anexo do veículo"
                                            onClick={(event) => {
                                                event.preventDefault();
                                                fileInputRef.current?.click();
                                            }}
                                            sx={{
                                                marginRight: 1,
                                                background: "#00B700",
                                                color: "#fff",
                                                "&:hover": { background: "#009900" },
                                            }}
                                        >
                                            <AddOutlined />
                                        </Fab>
                                    }
                                />
                            </Box>
                        )}
                    </Box>

                    <Typography
                        variant="caption"
                        sx={{ display: "block", marginTop: 1, color: "gray", fontFamily: "Prometo" }}
                    >
                        Máximo de {MAX_ATTACHMENTS} anexos de imagem. A remoção é efetivada ao
                        salvar.
                    </Typography>
                </Paper>
            )}

            <Box className="tw-mt-4 tw-border-t tw-border-black/10 tw-pt-4" sx={{ paddingTop: 2, marginTop: 2, borderTop: "1px solid rgba(0,0,0,0.10)" }}>
                <Paper
                    className="tw-flex tw-w-full tw-flex-col-reverse tw-gap-2 sm:tw-flex-row sm:tw-justify-end"
                    elevation={0}
                    sx={{ display: "flex", flexDirection: { xs: "column-reverse", sm: "row" }, justifyContent: "flex-end", width: { xs: "100%", sm: "auto" }, marginLeft: "auto", alignSelf: "flex-end", gap: 1, background: "transparent" }}
                >
                    <Button
                        type="button"
                        onClick={onClose}
                        disabled={saving}
                        className="btn btn-primary btn-block login-button-close tw-transition-transform tw-duration-200 hover:tw-translate-y-[-1px]"
                        sx={{ fontFamily: "Prometo", color: "#fff", marginRight: { xs: 0, sm: 1 }, width: { xs: "100%", sm: 130 } }}
                    >
                        <CloseOutlined sx={{ marginRight: 1 }} />
                        Fechar
                    </Button>

                    <Button
                        type="button"
                        onClick={handleSave}
                        disabled={isDisabled || saving}
                        className="btn btn-primary btn-block login-button tw-transition-transform tw-duration-200 hover:tw-translate-y-[-1px]"
                        sx={{ fontFamily: "Prometo", color: "#fff", width: { xs: "100%", sm: 130 } }}
                    >
                        {saving ? (
                            <CircularProgress size={20} sx={{ color: "#fff", marginRight: 1 }} />
                        ) : (
                            <SaveOutlined style={{ color: "#fff", marginRight: 5 }} />
                        )}

                        <Typography
                            className="forgot-password"
                            style={{
                                fontFamily: "Prometo",
                                fontSize: "16px",
                                color: "#ffff",
                            }}
                        >
                            {saving ? "Salvando..." : "Salvar"}
                        </Typography>
                    </Button>
                </Paper>
            </Box>
        </>
    );
};

export default VehicleParameter;
